import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  Box,
  Grid,
  IconButton,
  Menu,
  TableCell,
  Typography,
} from '@mui/material';
import {
  ColumnKey,
  Filter,
  PlanningColumn,
} from '../UserManagement/PlanningTable';
import { FilterAltOutlined } from '@mui/icons-material';
import TableFilter, { Operator } from './TableFilter';
import SortIcon from './SortIcon';
import { makeStyles } from '@mui/styles';

const useStyles = makeStyles(() => ({
  menu: {
    width: 'auto',
  },
  th: {
    paddingRight: 0,
    borderRight: '1px solid #e0e0e0',
  },
}));

type Props = {
  column: PlanningColumn;
  onSort: (id: ColumnKey) => void;
  activeFilter: Filter | undefined;
  sorting: {
    id: string;
    direction: 'asc' | 'desc';
  };
};

const ColumnHeaderCell = ({ column, onSort, sorting, activeFilter }: Props) => {
  const [isHovering, setHover] = useState(false);
  const [filter, setFilter] = useState({
    open: false,
    anchor: null,
    data: { id: column.id, op: undefined, value: undefined },
  });
  const Cell = useRef();
  const isSorted = useMemo(
    () => sorting.id === column.id,
    [sorting.id, column.id]
  );
  const classes = useStyles();

  const closeStateMenu = useCallback(() => setFilter({ ...filter, open: false, anchor: null }), [filter]);

  const onChangeFilter = useCallback(
    (id: ColumnKey, op: Operator, value: any) => {
      setFilter({ ...filter, data: { id, op, value }, open: false, anchor: null });
    }, [filter]
  );

  return (
    <>
      <TableCell
        ref={Cell}
        key={column.id}
        align={column.align}
        className={classes.th}
        style={{ minWidth: column.minWidth }}
      >
        <Box height={1} sx={{ display: 'flex', flex: 1 }}>
          <Grid
            container
            alignItems={'center'}
            justifyContent={'space-between'}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
          >
            <Grid container item xs={'auto'} alignItems={'stretch'}>
              <Typography variant={'bold'}>{column.label}</Typography>
            </Grid>
            <Grid
              item
              container
              xs={'auto'}
              alignItems={'stretch'}
              justifyContent={'flex-end'}
            >
                <IconButton
                  size={'small'}
                  onClick={() =>
                    setFilter({
                      ...filter,
                      open: !filter.open,
                      anchor: Cell.current,
                    })
                  }
                >
                    <FilterAltOutlined
                      fontSize={'small'}
                      color={activeFilter ? 'primary' : 'action'}
                    />
                </IconButton>
                <SortIcon
                  id={column.id}
                  isSorted={isSorted}
                  direction={sorting.direction}
                  onClick={onSort}
                />
            </Grid>
          </Grid>
        </Box>
      </TableCell>
      <Menu
        id="planning-filter-menu"
        aria-labelledby="planning-filter-menu"
        anchorEl={filter.anchor}
        open={filter.open}
        classes={{ paper: classes.menu }}
        onClose={closeStateMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <TableFilter
          column={column}
          onChange={onChangeFilter}
          initialFilter={filter.data}
        />
      </Menu>
    </>
  );
};

export default ColumnHeaderCell;
